/**
 * violex-cart-state.js — Geteilte Angebots-Fortschritts-Berechnung.
 *
 * EINE Quelle dafür, wie nah der Warenkorb am Gratis-Versand-Angebot ist
 * (Keychain + ein weiterer Artikel), damit Slide-in-Panel
 * (violex-cart-drawer.js), Warenkorb-Seite (violex-cart.js) und Header-Leiste
 * nicht je ihre eigene Kopie dieser Logik pflegen - genau die Art doppelter
 * Wahrheit, vor der Doku/tiefenanalyse-bestellprozess-elite-storage.md §2.1
 * warnt (Gratis-Versand-Status war schon einmal zwischen Storefront-JS und
 * echter Checkout-Function auseinandergelaufen).
 *
 * computeOfferProgress ist eine reine Funktion ohne Netzwerk-Zugriff: nimmt
 * ein bereits geladenes /cart.js-Objekt entgegen. window.VIOLEX_OFFER_READY/
 * -OFFER (violex-offer.js) bleiben die alleinige Quelle dafür, OB überhaupt
 * ein Angebot beworben werden soll - sie entscheidet nur, WIE NAH der
 * aktuelle Warenkorb am Ziel ist.
 *
 * Seit 29.08.2026 kommt fetchDomesticShippingRate() dazu, das SEHR WOHL das
 * Netz benutzt (siehe Kommentar dort). Es liegt aus demselben Grund hier wie
 * computeOfferProgress: Warenkorb-Seite und Slide-in-Panel brauchen beide
 * denselben Versandbetrag, und zwei Kopien dieser Abfrage wären genau die
 * doppelte Wahrheit, vor der die Doku warnt.
 *
 * §2.1-FIX (29.08.2026): "freeShippingActive" behauptet den kompletten
 * Zustand nur noch, wenn ZUSAETZLICH window.VIOLEX_FREE_SHIPPING_TRIGGER_ACTIVE
 * true ist - das liest layout/theme.liquid direkt aus demselben Produkt-
 * Metafeld (violex.free_shipping_trigger), das die echte Checkout-Function
 * prueft (extensions/violex-free-shipping). Composition allein (Keychain +
 * ein weiterer Artikel) reicht NICHT mehr fuer die "Versand entfaellt"-
 * Behauptung - vorher war das komplett unabhaengig vom echten Trigger.
 */
(function () {
  'use strict';

  /**
   * @param {{items: Array<{handle: string}>}} cart - Antwort von /cart.js
   * @param {boolean} offerActive - window.VIOLEX_OFFER_READY/-OFFER-Ergebnis
   * @param {string} keychainHandle
   * @returns {{active: boolean, freeShippingActive: boolean, hasKeychain: boolean, hasOther: boolean, percent: number, label: string}}
   */
  function computeOfferProgress(cart, offerActive, keychainHandle) {
    var result = {
      active: false,
      freeShippingActive: false,
      hasKeychain: false,
      hasOther: false,
      percent: 0,
      label: '',
    };
    if (!offerActive || !cart || !cart.items || cart.items.length === 0) return result;

    var hasKeychain = cart.items.some(function (i) { return i.handle === keychainHandle; });
    var hasOther = cart.items.some(function (i) { return i.handle !== keychainHandle; });
    // Bewusst window.* direkt statt als Parameter: der Wert kommt aus einem
    // synchronen Inline-Script in layout/theme.liquid, ist also schon
    // gesetzt, bevor irgendein deferred Script (inkl. dieser Datei) laeuft -
    // ein Parameter haette an drei Aufrufstellen dieselbe window.-Abfrage
    // nur verschoben, nicht vermieden.
    var triggerConfirmed = typeof window !== 'undefined' && window.VIOLEX_FREE_SHIPPING_TRIGGER_ACTIVE === true;

    result.active = true;
    result.hasKeychain = hasKeychain;
    result.hasOther = hasOther;
    result.freeShippingActive = hasKeychain && hasOther && triggerConfirmed;

    if (hasKeychain && hasOther && !triggerConfirmed) {
      // Warenkorb erfuellt die Bedingung, aber das echte Trigger-Metafield
      // bestaetigt es nicht (Definition/Wert fehlt noch, siehe Admin-App ->
      // Settings, oder wurde versehentlich vom Keychain-Produkt entfernt).
      // Bewusst NICHT "Versand entfaellt an der Kasse" behaupten (genau das
      // waere wieder das §2.1-Problem) - aber auch nicht mehr "noch 1
      // Artikel" sagen, wenn faktisch schon zwei verschiedene da sind.
      result.percent = 100;
      result.label = 'Keychain + Artikel im Warenkorb';
    } else if (result.freeShippingActive) {
      result.percent = 100;
      // "entfaellt an der Kasse", nicht "freigeschaltet"/"aktiv" (Doku §2.1,
      // dort woertlich als Sofortmassnahme empfohlen): "freigeschaltet"
      // behauptet einen bereits eingetretenen Zustand, "entfaellt an der
      // Kasse" eine Erwartung an den naechsten Schritt.
      result.label = 'Versand entfällt an der Kasse';
    } else if (hasKeychain || hasOther) {
      // Beide Faelle (nur Keychain drin / nur ein anderer Artikel drin)
      // brauchen exakt noch EINEN weiteren Artikel - gleicher Text, gleicher
      // Balkenstand, nur die Richtung im Warenkorb-Upsell unterscheidet sich
      // (siehe maybeRenderUpsell in violex-cart.js).
      result.percent = 50;
      result.label = 'Noch 1 Artikel bis Gratis-Versand';
    } else {
      // Kommt praktisch nicht vor - ist cart.items nicht leer, ist immer
      // mindestens eine der beiden Bedingungen erfuellt. Nur zur Sicherheit.
      result.percent = 0;
      result.label = 'Noch 2 Artikel bis Gratis-Versand';
    }

    return result;
  }

  // ── Echte Versandkosten aus den Shopify-Versandzonen ──────────────────────
  // 29.08.2026 (Wunsch Tomas: "wie kann ich fuer die order page sowie
  // checkout die shipping preise uebernehmen"). Vorher zeigten Warenkorb-
  // Seite und Panel einen fest im Theme eingetragenen Schaetzwert (6.90),
  // der mit keiner echten Stufe uebereinstimmte - die Domestic-Tarife sind
  // gewichtsabhaengig (B-Post 3.00 bis PostPac Economy 7.50 und mehr).
  //
  // /cart/shipping_rates.json liefert exakt die im Admin gepflegten Tarife
  // fuer den aktuellen Warenkorb. Zwei bewusste Einschraenkungen:
  //
  //  1. Zielland fest Schweiz. Der Tarif haengt am Land (CH 3.00 vs. EU 4.50
  //     bei gleichem Gewicht), die Adresse kennt der Shop aber erst im
  //     Checkout. Deshalb rechnet die Seite mit CH und sagt das auch dazu
  //     (Hinweistext in sections/cart-with-offers.liquid).
  //  2. Der Endpunkt kennt den Gratis-Versand-Rabatt NICHT - der kommt aus
  //     der Shopify Function (extensions/violex-free-shipping) und greift
  //     erst im Checkout. Bei Keychain + Artikel liefert er weiterhin den
  //     vollen Satz. Die Gratis-Versand-Logik der Aufrufer muss deshalb
  //     WEITERHIN oben drueber liegen, sonst zeigt die Seite Versandkosten,
  //     wo der Kunde nichts zahlt (getestet 29.08.2026).
  //
  // Die PLZ ist ein beliebiger gueltiger CH-Wert (6005, der Firmensitz) -
  // die Domestic-Tarife sind rein gewichtsbasiert, nicht regional gestaffelt.
  var rateCache = { key: null, promise: null };

  function fetchDomesticShippingRate(cart) {
    if (!cart || !cart.item_count) return Promise.resolve(null);

    // Der Tarif haengt am Gewicht - solange sich das nicht aendert, ist die
    // Antwort dieselbe und muss nicht erneut geholt werden.
    var key = cart.total_weight + ':' + cart.item_count;
    if (rateCache.key === key && rateCache.promise) return rateCache.promise;

    var url =
      '/cart/shipping_rates.json?shipping_address[country]=Switzerland' +
      '&shipping_address[zip]=6005';

    var p = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.shipping_rates || !data.shipping_rates.length) return null;
        // Guenstigsten Tarif nehmen: "ab CHF X" soll der niedrigste sein,
        // den der Kunde bekommen kann, nicht irgendeiner aus der Liste.
        return data.shipping_rates.reduce(function (min, rate) {
          var preis = parseFloat(rate.price);
          if (isNaN(preis)) return min;
          return min === null || preis < min ? preis : min;
        }, null);
      })
      // Netzwerkfehler duerfen die Seite nicht kaputtmachen: null bedeutet
      // "kein echter Wert verfuegbar", die Aufrufer bleiben dann beim
      // bisherigen Schaetzwert aus den Section-Einstellungen.
      .catch(function () { return null; });

    rateCache = { key: key, promise: p };
    return p;
  }

  window.ViolexCartState = {
    computeOfferProgress: computeOfferProgress,
    fetchDomesticShippingRate: fetchDomesticShippingRate,
  };
})();
